/**
 * ClarificationModal Component
 * 
 * Modal shown when LLM needs clarification.
 * Displays question and selectable options.
 */

import { HelpCircle, X } from 'lucide-react';
import { Button } from '../../../../shared/components/ui/Button';
import { Suggestion } from '../../types';

interface ClarificationModalProps {
    question: string;
    options: Suggestion[];
    onSelect: (option: Suggestion) => void;
    onClose: () => void;
}

export function ClarificationModal({
    question,
    options,
    onSelect,
    onClose,
}: ClarificationModalProps) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
            <div role="dialog" aria-modal="true" aria-label="Necesito más información" className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                            <HelpCircle className="text-white" size={20} />
                        </div>
                        <h3 className="font-semibold text-slate-900">Necesito más información</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-amber-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-slate-400" />
                    </button>
                </div>

                {/* Question */}
                <div className="p-6">
                    <p className="text-slate-700 text-lg mb-6">{question}</p>

                    {/* Options */}
                    <div className="space-y-3">
                        {options.map((option, idx) => (
                            <button
                                key={idx}
                                onClick={() => onSelect(option)}
                                className="w-full text-left px-4 py-3 bg-slate-50 hover:bg-violet-50 rounded-xl border border-slate-200 hover:border-violet-300 transition-all group"
                            >
                                <span className="text-slate-700 group-hover:text-violet-700 font-medium">
                                    {option.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        className="w-full"
                    >
                        Cancelar y escribir de otra forma
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default ClarificationModal;
